import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

import { cancelAtHomeBooking, fetchAtHomeBookings } from 'api/atHome'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import CloseSessionDialog, { canClose } from './CloseSessionDialog'
import { bookingRow, fmtDate, fmtTime, titleCase } from './format'
import MoveSessionDialog from './MoveSessionDialog'

/**
 * The sessions of one regular booking (a module plan today, a monthly plan
 * later), opened from its row under Bookings → Regular. Each session can be
 * moved (same teacher, another time) or cancelled on its own; payment and
 * cancelling the whole plan stay on the plan row.
 */
const PlanSessionsDialog = ({ plan, open, onClose, onChanged }) => {
  const [rows, setRows] = React.useState(null)
  const [moving, setMoving] = React.useState(null)
  const [cancelling, setCancelling] = React.useState(null)
  const [reason, setReason] = React.useState('')
  const [closing, setClosing] = React.useState(null)

  const load = React.useCallback(async () => {
    if (!plan) return
    const all = await fetchAtHomeBookings({ view: 'all' })
    setRows(
      all
        .filter((b) => b.plan === plan.uuid)
        .map(bookingRow)
        .sort((a, b) => (a.plan_index || 0) - (b.plan_index || 0))
    )
  }, [plan])

  React.useEffect(() => {
    if (!open) return
    setRows(null)
    load().catch(() => setRows([]))
  }, [open, load])

  const changed = () => {
    load().catch(() => {})
    onChanged()
  }

  const doCancel = async () => {
    try {
      await cancelAtHomeBooking(cancelling.uuid, reason)
      eventAlert('Session cancelled', constants.SUCCESS)
      setCancelling(null)
      setReason('')
      changed()
    } catch (error) {
      // request.js surfaces the server's reason
    }
  }

  const statusOf = (b) => {
    if (b.status === 'booked' && b.report?.started_at) return 'Started'
    // Past and never marked: not attended, closed from here (owner, 2026-10-21).
    if (canClose(b)) return 'Not marked'
    return titleCase(b.status)
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          {plan
            ? `${plan.childName} · ${plan.moduleLabel || plan.categoryName}`
            : 'Sessions'}
        </DialogTitle>
        <DialogContent>
          {plan && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {plan.frequencyLabel} · {plan.scheduleLabel} · {plan.teacherLabel}{' '}
              · {plan.datesLabel}. Move, cancel or close one session here;
              payment and cancelling the whole booking are on the row outside.
            </Typography>
          )}
          {rows === null && <DialogContentText>Loading…</DialogContentText>}
          {rows && rows.length === 0 && plan?.status === 'pending' && (
            <DialogContentText>
              Nothing is booked yet. The sessions are created the moment the
              parent pays.
            </DialogContentText>
          )}
          {rows && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Session</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((b) => (
                  <TableRow key={b.uuid}>
                    <TableCell>{b.plan_index}</TableCell>
                    <TableCell>{b.sessionShort || '—'}</TableCell>
                    <TableCell>{fmtDate(b.date)}</TableCell>
                    <TableCell>
                      {fmtTime(b.startTime)} – {fmtTime(b.endTime)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusOf(b)} />
                      {b.reschedules?.length ? (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ marginLeft: 6 }}
                        >
                          moved {b.reschedules.length}×
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                      {canClose(b) && (
                        <Button size="small" onClick={() => setClosing(b)}>
                          Close session
                        </Button>
                      )}
                      {b.status === 'booked' && (
                        <>
                          <Button size="small" onClick={() => setMoving(b)}>
                            Reschedule
                          </Button>
                          <Button size="small" onClick={() => setCancelling(b)}>
                            Cancel
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <CloseSessionDialog
        row={closing}
        onClose={() => setClosing(null)}
        onDone={() => {
          setClosing(null)
          changed()
        }}
      />
      <MoveSessionDialog
        booking={moving}
        open={Boolean(moving)}
        onClose={() => setMoving(null)}
        onMoved={changed}
      />
      <Dialog
        open={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel this session?</DialogTitle>
        <DialogContent>
          {cancelling && (
            <DialogContentText>
              {cancelling.sessionShort || 'This session'} on{' '}
              {cancelling.dateLabel}, {cancelling.timeLabel}. Only this session
              is cancelled; the rest of the booking stays.
            </DialogContentText>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelling(null)}>Keep</Button>
          <Button onClick={doCancel} color="primary" variant="contained">
            Cancel session
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

PlanSessionsDialog.propTypes = {
  plan: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired
}

export default PlanSessionsDialog
